import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Num, Ring, useReady } from "../admin/adminCharts";
import useWidgetLayout from "../widgets/useWidgetLayout";
import { CustomizeButtons, EditBar, SaveErrorBar, WidgetGrid, WidgetLibrary } from "../widgets/WidgetGrid";
import { WEEK_HOURS_CAP } from "../shared/classData";
import { INSTRUCTOR_DEFAULTS, INSTRUCTOR_WIDGETS } from "./instructorWidgets";
import "./instructorDashboard.css";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function Hero({ data, ready, L }) {
  const { header, hero } = data;
  const { live, next } = hero;
  const left = hero.sessionsToday - hero.sessionsDone;
  return (
    <header className="ix-hero" data-testid="instructor-hero"><div className="ix-hero-in">
      <div className="ix-top">
        <div className="t"><span className="eb">{header.eyebrow}</span><h1>{header.greeting}</h1></div>
        <CustomizeButtons L={L} dark />
      </div>
      <div className="hm4">
        <div className="hk" data-testid="instructor-kpi-now">
          <span className="eb">Teaching now</span>
          <div className="row"><span className="v"><span style={{ fontSize: 20, lineHeight: 1.2 }}>{live ? live.title : "Not teaching right now"}</span></span>{live && <span className="ix-live">Live</span>}</div>
          <span className="sub">{live ? <><b>{live.start} – {live.end}</b>{live.location ? ` · ${live.location}` : ""} · {live.learners} enrolled</> : next ? <>Next: <b>{next.title}</b> at {next.start}</> : "No more sessions today"}</span>
        </div>
        <div className="hk" data-testid="instructor-kpi-sessions" style={{ animationDelay: "60ms" }}>
          <span className="eb">Sessions today</span>
          <div className="row"><span className="v"><Num to={hero.sessionsToday} delay={60} /></span>
            <div className="split" aria-hidden="true">{data.today.map((s) => <i key={s.id} style={{ flexGrow: ready ? 1 : 0, background: s.state === "done" ? "var(--color-accent-lime)" : s.state === "live" ? "#fff" : "var(--color-hairline-violet)" }}></i>)}</div></div>
          <span className="sub">{hero.sessionsToday === 0 ? "A free day" : <>{hero.sessionsDone} complete{next ? <> · next at <b>{next.start}</b></> : left > 0 ? " · one under way" : ""}</>}</span>
        </div>
        <div className="hk" data-testid="instructor-kpi-signoff" style={{ animationDelay: "120ms" }}>
          <span className="eb">Awaiting sign-off</span>
          <div className="row"><span className="v"><Num to={hero.signoffs} delay={120} /></span></div>
          <span className="sub">{hero.signoffs ? `Across ${plural(hero.signoffClasses, "class")}` : "You are all caught up"}</span>
        </div>
        <div className="hk" data-testid="instructor-kpi-hours" style={{ animationDelay: "180ms" }}>
          <span className="eb">Hours this week</span>
          <div className="row"><span className="v"><Num to={hero.hoursThisWeek} delay={180} /><small>of {WEEK_HOURS_CAP}</small></span>
            <Ring pct={Math.min(100, hero.hoursThisWeek / WEEK_HOURS_CAP * 100)} size={44} sw={5} color="var(--color-accent-lime)" delay={350} /></div>
          <span className="sub">{hero.hoursThisWeek > WEEK_HOURS_CAP ? `${hero.hoursThisWeek - WEEK_HOURS_CAP} hours over the target` : `${plural(hero.hoursLeft, "hour")} of capacity left`}</span>
        </div>
      </div>
    </div></header>
  );
}

/**
 * Instructor landing dashboard: a dark hero plus a customizable widget grid.
 * @param {{ data: object, tenantId: string, now?: Date }} props `data` is the buildInstructorModel view model.
 */
export default function InstructorDashboard({ data, tenantId, now: nowProp }) {
  const ready = useReady(300);
  const now = useMemo(() => nowProp || new Date(), [nowProp]);
  const L = useWidgetLayout({ role: "instructor", tenantId, registry: INSTRUCTOR_WIDGETS, defaults: INSTRUCTOR_DEFAULTS });
  const ctx = useMemo(() => ({ m: data, tenantId, now, navigate: data.navigate }), [data, tenantId, now]);
  if (data.loading || L.loading) return <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>;
  return (
    <div className="ix" data-testid="instructor-dashboard">
      <Hero data={data} ready={ready} L={L} />
      <main className="ix-wrap">
        <div className="ix-grid">
          <SaveErrorBar L={L} />
          <EditBar L={L} />
          <WidgetGrid L={L} ctx={ctx} />
        </div>
      </main>
      <WidgetLibrary L={L} roleLabel="Instructor" ctx={ctx} />
    </div>
  );
}
